// AUPRÉA Platform — Full stack
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { auth, conseillersApi, leadsApi, dashboardApi, commissionsApi } from "./api";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
import {
  C, fmtEur, Badge, Btn, Input, Modal, FormField, Alert, Spinner, Select,
  prestationsApi, leadsExtraApi,
  CANAUX,
} from "./shared";
import { PrestationsAdmin } from "./PrestationsAdmin";
import { ReglagesAdmin } from "./ReglagesAdmin";
import ModelesAdmin from "./Modeles";
import ValidationsAdmin from "./Validations";
import { ParcoursClient } from "./ParcoursClient";
import { ParcoursList } from "./ParcoursList";
import { Disponibilites } from "./Disponibilites";
import { FacturesPage } from "./Factures";
import FacturationAdmin from "./Facturation";
import MissionsPage from "./Missions";
import BackOffice from "./BackOffice";
import { MesCommissionsV2, CommissionsAdminV2 } from "./CommissionsV2";
import { LeadDetail } from "./LeadDetail";
import { LeadsPage, DeclarerLeadModal } from "./Leads";
import CommissionsValider from "./CommissionsValider";
import { KitTerrainAdmin, KitTerrain } from "./KitTerrain";
import { ConseillerDetail } from "./ConseillerDetail";
import { AdminDashboardV2, ConseillerDashboardV2, DossiersAdmin } from "./Dashboards";
import { CatalogueConseiller } from "./Catalogue";
import { RgpdDemandes } from "./RgpdAdmin";
import { MonEquipe, CandidaturesAdmin, ReseauAdminV2, RejoindrePage } from "./Reseau";
import { EspaceIntegration } from "./Integration";
import { IntegrationsAdmin, PiecesAControler, MesRecrues } from "./IntegrationsAdmin";
import { ModelesContrats, ContratsSignes } from "./ContratsAdmin";
import { SignatureContrats } from "./ContratsSignature";
import { MaCotisation } from "./CotisationPaiement";
import { CotisationsAdminV2 } from "./CotisationsAdminV2";
import BilansAdmin from "./BilansAdmin";
import MesFichesServices from "./MesFichesServices";
import FichesServicesAdmin from "./FichesServicesAdmin";
import ReformulationsAdmin from "./ReformulationsAdmin";
import NotificationsAdmin, { ClocheNotifs } from "./NotificationsAdmin";
import PartenairesAdmin from "./PartenairesAdmin";
import AnnuairePartenaires from "./AnnuairePartenaires";
import ServicesAdmin from "./ServicesAdmin";
import MesServices from "./ServicesConsultant";
import { MonEquipeRecruteur } from "./MonEquipeRecruteur";
import { SetupPasswordPage, ResetPasswordPage, ForgotPasswordModal, AuthSuccess } from "./AuthPages";
import { NetworkTree, MonCompte, InvitationLinkModal, PendingInvitations, ZONES, ROLES_USER } from "./NetworkPages";

// ── Small components ─────────────────────────────────────────────────────────
const Avatar = ({ initials, color, size = 36 }) => (
  <div style={{ width: size, height: size, borderRadius: "50%", background: (color || C.muted) + "22", color: color || C.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.33, fontWeight: 700, flexShrink: 0 }}>{initials}</div>
);

const KpiCard = ({ label, value, delta, deltaUp, accentColor }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`, padding: 18, position: "relative", overflow: "hidden" }}>
    <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accentColor }} />
    <div style={{ fontSize: 11, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 8 }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, marginBottom: 4, color: accentColor }}>{value}</div>
    <div style={{ fontSize: 11, fontWeight: 500, color: deltaUp ? C.green : deltaUp === false ? C.red : C.muted }}>{delta}</div>
  </div>
);

const ProgBar = ({ value, max, color }) => {
  const pct = Math.min(100, Math.round((value || 0) / (max || 1) * 100));
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: C.border, borderRadius: 3, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 3, transition: "width .4s" }} />
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, minWidth: 36, textAlign: "right", color }}>{pct}%</div>
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: C.mid, textTransform: "uppercase", letterSpacing: ".09em", margin: "22px 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
    {children}
    <div style={{ flex: 1, height: .5, background: C.border }} />
  </div>
);

const TableCard = ({ title, action, children }) => (
  <div style={{ background: "#fff", borderRadius: 12, border: `.5px solid ${C.border}`, overflow: "hidden", marginBottom: 20 }}>
    {(title || action) && (
      <div style={{ padding: "12px 18px", borderBottom: `.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.navy }}>{title}</div>
        {action}
      </div>
    )}
    <div style={{ overflowX: "auto" }}>{children}</div>
  </div>
);

// ── CONSEILLER MODAL ─────────────────────────────────────────────────────────
const ConseillerModal = ({ onClose, onSaved, conseillers, onCreated }) => {
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", tel: "", zone: ZONES[0], formule: "Conseiller",
    parrain: "", role_user: "conseiller",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async () => {
    if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) return setErr("Prénom, nom et email requis");
    setSaving(true); setErr("");
    try {
      const result = await conseillersApi.create({ ...form, parrain: form.parrain || null });
      onSaved();
      onClose();
      onCreated && onCreated(result);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };
  return (
    <Modal title="Ajouter un consultant" onClose={onClose}
      footer={<><Btn onClick={onClose}>Annuler</Btn><Btn variant="gold" disabled={saving} onClick={submit}>{saving ? "..." : "Créer & générer le lien d'invitation"}</Btn></>}>
      {err && <Alert type="error"><span>⚠</span><span>{err}</span></Alert>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="Prénom" required><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} placeholder="Daisy" /></FormField>
          <FormField label="Nom" required><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} placeholder="VAILLANT" /></FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="Email" required><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></FormField>
          <FormField label="Téléphone"><Input value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })} placeholder="+590 690…" /></FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="Zone">
            <Select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}>
              {ZONES.map(z => <option key={z}>{z}</option>)}
            </Select>
          </FormField>
          <FormField label="Rôle">
            <Select value={form.role_user} onChange={(e) => setForm({ ...form, role_user: e.target.value })}>
              {ROLES_USER.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </Select>
          </FormField>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <FormField label="Formule de cotisation">
            <Select value={form.formule} onChange={(e) => setForm({ ...form, formule: e.target.value })}>
              <option value="Starter">Starter — 149 €/an</option>
              <option value="Conseiller">Consultant — 249 €/an</option>
              <option value="Premium">Premium — 349 €/an</option>
            </Select>
          </FormField>
          <FormField label="Parrain">
            <Select value={form.parrain} onChange={(e) => setForm({ ...form, parrain: e.target.value })}>
              <option value="">Direct AUPRÉA</option>
              {conseillers.map(c => <option key={c.id} value={c.id}>{c.prenom || ""} {c.nom} ({c.agrement || c.id})</option>)}
            </Select>
          </FormField>
        </div>
        <div style={{ background: C.goldlt, border: `1px solid ${C.gold}55`, borderRadius: 8, padding: 12, fontSize: 12, color: C.navy }}>
          💡 Un lien d'invitation (48 h) sera généré : le consultant définira lui-même son mot de passe.
          <br />N° d'agrément et code promo sont attribués automatiquement.
        </div>
      </div>
    </Modal>
  );
};

// ── SIDEBAR ───────────────────────────────────────────────────────────────────
const NAV_ADMIN = [
  { section: "Principal" },
  { id: "dashboard", label: "Vue d'ensemble", icon: "📊" },
  { id: "backoffice", label: "Back-office", icon: "🗃️" },
  { id: "dossiers-admin", label: "Tous les dossiers", icon: "📁" },
  { id: "virements", label: "Encaissements à valider", icon: "🏦" },
  { id: "validations", label: "Validations", icon: "✅" },
  { id: "missions", label: "Missions", icon: "🧭" },
  { id: "bilans-clients", label: "Bilans & clients", icon: "📈" },
  { section: "Finances" },
  { id: "commissions_valider", label: "Commissions à valider", icon: "☑️" },
  { id: "commissions", label: "Commissions", icon: "💶" },
  { id: "cotisations", label: "Cotisations", icon: "📑" },
  { id: "factures", label: "Facturation", icon: "🧾" },
  { section: "Réseau & Équipe" },
  { id: "conseillers", label: "Consultants", icon: "👥" },
  { id: "candidatures", label: "Candidatures", icon: "📬" },
  { id: "integrations", label: "Intégrations", icon: "🧩" },
  { id: "pieces", label: "Pièces à contrôler", icon: "🪪" },
  { id: "modeles-contrats", label: "Modèles de contrats", icon: "📜" },
  { id: "contrats-signes", label: "Contrats signés", icon: "✍️" },
  { id: "partenaires", label: "Partenaires", icon: "🤝" },
  { id: "reseau-tree", label: "Vision Réseau", icon: "🌐" },
  { id: "invitations", label: "Invitations", icon: "📧" },
  { section: "Configuration" },
  { id: "prestations", label: "Catalogue", icon: "🛍️" },
  { id: "fiches-services-admin", label: "Fiches services", icon: "📘" },
  { id: "reformulations-admin", label: "Reformulations", icon: "💬" },
  { id: "services-admin", label: "Parcours de service", icon: "🧭" },
  { id: "notifications-admin", label: "Notifications", icon: "🔔" },
  { id: "parametres", label: "Réglages", icon: "⚙️" },
  { id: "modeles", label: "Modèles de documents", icon: "📄" },
  { id: "rgpd-admin", label: "RGPD", icon: "🔐" },
  { id: "kit-admin", label: "Kit terrain", icon: "🗂️" },
  { id: "leads", label: "Leads & CRM", icon: "📋" },
];

const NAV_CONSEILLER = [
  { section: "Mon espace" },
  { id: "dashboard", label: "Tableau de bord", icon: "📊" },
  { id: "parcours", label: "Mes rendez-vous & dossiers", icon: "📅" },
  { id: "missions", label: "Mes missions", icon: "🎯" },
  { section: "Revenus" },
  { id: "commissions", label: "Mes commissions", icon: "💶" },
  { id: "cotisations", label: "Ma cotisation", icon: "📑" },
  { id: "factures", label: "Mes factures", icon: "🧾" },
  { section: "Réseau" },
  { id: "reseau", label: "Mon équipe", icon: "🌐" },
  { id: "mes-recrues", label: "Mes recrues", icon: "🧩" },
  { id: "mes-contrats", label: "Mes contrats", icon: "✍️" },
  { id: "leads", label: "Mes leads", icon: "📋" },
  { section: "Ressources" },
  { id: "catalogue", label: "Catalogue prestations", icon: "🛍️" },
  { id: "fiches-services", label: "Mes fiches services", icon: "📘" },
  { id: "mes-services", label: "Mes services à réaliser", icon: "🧭" },
  { id: "annuaire-partenaires", label: "Annuaire des partenaires", icon: "🤝" },
  { id: "ressources", label: "Kit terrain", icon: "🗂️" },
];

const Sidebar = ({ role, user, activePage, onNavigate, onLogout, badges, open }) => {
  const nav = role === "admin" ? NAV_ADMIN : NAV_CONSEILLER;
  return (
    <nav className={`aup-sidebar${open ? " open" : ""}`} data-testid="app-sidebar" style={{ width: 240, flexShrink: 0, background: C.navy, display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh", overflow: "hidden" }}>
      <div style={{ padding: "22px 20px 18px", borderBottom: `1px solid rgba(255,255,255,.06)` }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, color: "#fff", letterSpacing: ".04em" }}>AUPRÉA</div>
        <div style={{ fontSize: 10, color: C.gold, fontStyle: "italic", marginTop: 2 }}>vous accompagne</div>
        <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", background: "rgba(201,168,76,.12)", border: `1px solid rgba(201,168,76,.25)`, borderRadius: 20, padding: "3px 10px", fontSize: 10, fontWeight: 600, color: C.gold, letterSpacing: ".06em" }}>
          {role === "admin" ? "ADMIN" : "CONSULTANT"}
        </div>
      </div>
      <div style={{ flex: 1, padding: "10px 10px", overflowY: "auto" }}>
        {nav.map((item, i) => item.section ? (
          <div key={i} style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,.25)", letterSpacing: ".12em", textTransform: "uppercase", padding: "12px 10px 5px" }}>{item.section}</div>
        ) : (
          <div key={item.id} onClick={() => onNavigate(item.id)} style={{
            display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 8,
            cursor: "pointer", marginBottom: 2, position: "relative",
            background: activePage === item.id ? "rgba(201,168,76,.12)" : "transparent",
            color: activePage === item.id ? "#fff" : "rgba(255,255,255,.55)",
            fontWeight: activePage === item.id ? 600 : 400, fontSize: 13,
            transition: "background .15s, color .15s",
          }}>
            {activePage === item.id && <div style={{ position: "absolute", left: 0, top: 6, bottom: 6, width: 3, background: C.gold, borderRadius: 2 }} />}
            <span>{item.icon}</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            {badges?.[item.id] > 0 && <span style={{ background: C.red, color: "#fff", borderRadius: 10, padding: "1px 7px", fontSize: 10, fontWeight: 700 }}>{badges[item.id]}</span>}
          </div>
        ))}
      </div>
      <div style={{ padding: "14px 16px 20px", borderTop: `1px solid rgba(255,255,255,.06)` }}>
        <div onClick={() => onNavigate("mon-compte")} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, cursor: "pointer", padding: 4, borderRadius: 8 }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,.05)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
          <div style={{ width: 34, height: 34, borderRadius: "50%", background: C.gold, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: C.navy, flexShrink: 0 }}>{user.initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.nom}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,.4)" }}>{user.title}</div>
          </div>
          <span style={{ fontSize: 12, color: "rgba(255,255,255,.4)" }}>⚙</span>
        </div>
        <button onClick={onLogout} style={{ width: "100%", background: "rgba(255,255,255,.05)", border: `1px solid rgba(255,255,255,.1)`, borderRadius: 8, padding: 7, color: "rgba(255,255,255,.5)", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Déconnexion</button>
      </div>
    </nav>
  );
};

const PAGE_TITLES = {
  dashboard: "Tableau de bord", leads: "Leads & CRM", conseillers: "Consultants", modeles: "Modèles de documents", validations: "Validations",
  commissions: "Commissions", commissions_valider: "Commissions à valider", cotisations: "Cotisations", communications: "Communications",
  rapports: "Rapports", parametres: "Paramètres",
  reseau: "Mon équipe",
  ressources: "Kit terrain", catalogue: "Catalogue des prestations", prestations: "Gestion des prestations",
  "kit-admin": "Kit terrain — Gestion des documents",
  "reseau-tree": "Vision Réseau",
  candidatures: "Candidatures recrues",
  invitations: "Invitations & réinitialisations",
  "mon-compte": "Mon compte",
  parcours: "Parcours client & rendez-vous",
  disponibilites: "Mes disponibilités",
  missions: "Missions",
  backoffice: "Back-office — pipeline, indicateurs, audit",
  factures: "Facturation",
  "bilans-clients": "Bilans & clients",
  "dossiers-admin": "Tous les dossiers",
  virements: "Encaissements à valider",
  "rgpd-admin": "RGPD — Demandes clients",
  "mon-integration": "Mon parcours d'intégration",
  integrations: "Intégrations des nouveaux consultants",
  pieces: "Pièces à contrôler",
  "mes-recrues": "Mes recrues",
  "fiches-services": "Mes fiches services",
  "fiches-services-admin": "Fiches services",
  "reformulations-admin": "Reformulations",
  "services-admin": "Parcours de service",
  "notifications-admin": "Notifications",
  partenaires: "Partenaires professionnels",
  "annuaire-partenaires": "Annuaire des partenaires",
  "mes-services": "Mes services à réaliser",
  "modeles-contrats": "Modèles de contrats",
  "contrats-signes": "Contrats signés du réseau",
  "mes-contrats": "Mes contrats",
};

const Topbar = ({ page, role, onAction, nbValidations = 0, onNavigate, onMenu }) => (
  <div className="aup-topbar" style={{ background: "#fff", borderBottom: `.5px solid ${C.border}`, padding: "0 28px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
      <button className="aup-burger" onClick={onMenu} aria-label="Menu" data-testid="menu-mobile-btn" style={{ display: "none", background: "none", border: `1px solid ${C.border}`, borderRadius: 8, width: 36, height: 36, alignItems: "center", justifyContent: "center", fontSize: 18, cursor: "pointer", color: C.navy }}>☰</button>
      <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 16, fontWeight: 600, color: C.navy }}>{PAGE_TITLES[page] || page}</div>
      <div className="aup-topbar-date" style={{ fontSize: 12, color: C.muted }}>{new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
      </div>
    </div>
    <div className="aup-topbar-actions" style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {role !== "admin" && page !== "leads" && <Btn variant="gold" onClick={onAction} data-testid="topbar-declarer-lead">+ Déclarer un lead</Btn>}
      {role === "admin" && (
        <ClocheNotifs onNavigate={onNavigate} />
      )}
    </div>
  </div>
);

// ── ADMIN DASHBOARD ───────────────────────────────────────────────────────────
const AdminDashboard = ({ data, conseillers }) => {
  if (!data) return <Spinner />;
  return (
    <>
      <Alert type="info">
        <span>💡</span>
        <span><strong>Stratégie 30 jours :</strong> objectif 15 consultants et 10 000 € CA — {data.actifs} consultants actifs, {fmtEur(data.totalCA)} de CA ce mois.</span>
      </Alert>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <KpiCard label="CA du mois" value={fmtEur(data.totalCA)} delta="↑ +18% vs mois dernier" deltaUp={true} accentColor={C.teal} />
        <KpiCard label="Consultants actifs" value={data.actifs} delta={`Objectif : 15 · ${data.actifs}/15`} accentColor={C.blue} />
        <KpiCard label="Leads déclarés" value={data.leadsCount} delta={`${data.convertis} convertis · ${data.tauxConv}%`} deltaUp={true} accentColor={C.amber} />
        <KpiCard label="Commissions à verser" value={fmtEur(data.totalComm)} delta="Versement le 30/06" accentColor={C.golddm} />
        <KpiCard label="Taux de conversion" value={`${data.tauxConv}%`} delta="↑ Objectif : 50%" deltaUp={true} accentColor={C.purp} />
        <KpiCard label="Cotisations perçues" value={fmtEur(data.cotisations)} delta={`${data.actifs} consultants · 249 € moy.`} accentColor={C.green} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div>
          <SectionLabel>Progression objectifs du mois</SectionLabel>
          <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
            {[["CA généré", C.teal, data.totalCA, 10000], ["Consultants actifs", C.blue, data.actifs, 15], ["Leads convertis", C.amber, data.convertis, 20], ["Posts publiés", C.purp, 12, 20]].map(([l, c, v, o]) => (
              <div key={l} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                  <span style={{ fontWeight: 500 }}>{l}</span>
                  <span style={{ fontWeight: 600, color: c }}>{o > 100 ? fmtEur(v) : v} / {o > 100 ? fmtEur(o) : o}</span>
                </div>
                <ProgBar value={v} max={o} color={c} />
              </div>
            ))}
          </div>
        </div>
        <div>
          <SectionLabel>Activité récente</SectionLabel>
          <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 16 }}>
            {(data.activite || []).map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 10, paddingBottom: 14, position: "relative" }}>
                {i < data.activite.length - 1 && <div style={{ position: "absolute", left: 12, top: 28, bottom: 0, width: 2, background: C.border }} />}
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: a.type === "success" ? C.greenlt : a.type === "info" ? C.bluelt : C.gray2, border: `1.5px solid ${a.type === "success" ? C.green : a.type === "info" ? C.blue : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0, color: a.type === "success" ? C.green : a.type === "info" ? C.blue : C.mid }}>
                  {a.type === "success" ? "✓" : a.type === "info" ? "★" : "↻"}
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 500, color: C.text }}>{a.text}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <SectionLabel>Top consultants ce mois</SectionLabel>
      <TableCard>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>{["Consultant", "Zone", "Palier", "Ventes", "CA", "Commission", "Statut"].map(h => (
            <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2, whiteSpace: "nowrap" }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {[...conseillers].filter(c => c.statut === "Actif").sort((a, b) => b.ca - a.ca).map(c => (
              <tr key={c.id} style={{ borderBottom: `.5px solid ${C.border}` }}>
                <td style={{ padding: "12px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar initials={c.initials} color={c.color} size={30} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{c.nom}</div>
                      <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{c.id}</div>
                    </div>
                  </div>
                </td>
                <td style={{ padding: "12px 16px", fontSize: 12 }}>{c.zone}</td>
                <td style={{ padding: "12px 16px" }}><Badge text={c.palier} /></td>
                <td style={{ padding: "12px 16px", fontWeight: 600 }}>{c.ventes}</td>
                <td style={{ padding: "12px 16px", fontWeight: 600, color: C.teal }}>{fmtEur(c.ca)}</td>
                <td style={{ padding: "12px 16px", fontWeight: 600, color: C.golddm }}>{fmtEur(c.comm)}</td>
                <td style={{ padding: "12px 16px" }}><Badge text="Actif" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
};

// ── CONSEILLERS LIST ──────────────────────────────────────────────────────────
const ConseillersList = ({ conseillers, onAdd, onDelete, onOpen, isAdmin }) => {
  const [q, setQ] = useState("");
  const filtered = conseillers.filter(c => !q || c.nom.toLowerCase().includes(q.toLowerCase()) || c.id.toLowerCase().includes(q.toLowerCase()));
  const parrainOf = (cid) => conseillers.find(x => x.id === cid);
  const filleulsOf = (id) => conseillers.filter(x => x.parrain === id);
  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: C.gray2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 12px", flex: 1, maxWidth: 300 }}>
          <span style={{ color: C.muted }}>🔍</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un consultant..." style={{ border: "none", background: "transparent", fontSize: 13, outline: "none", fontFamily: "inherit", width: "100%" }} />
        </div>
        {isAdmin && <Btn variant="gold" onClick={onAdd}>+ Nouveau consultant</Btn>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
        {filtered.map(c => {
          const parr = parrainOf(c.parrain);
          const nbFilleuls = filleulsOf(c.id).length;
          return (
            <div key={c.id} onClick={() => isAdmin && onOpen(c.id)} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 12, padding: 16, cursor: isAdmin ? "pointer" : "default", transition: "box-shadow .15s, transform .1s" }}
              onMouseEnter={(e) => { if (isAdmin) e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,.08)"; }}
              onMouseLeave={(e) => { if (isAdmin) e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Avatar initials={c.initials} color={c.color} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.prenom && !c.nom.includes(c.prenom) ? `${c.prenom} ${c.nom}` : c.nom}</div>
                  <div style={{ fontSize: 10, color: C.muted, fontFamily: "monospace" }}>{c.id}</div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
                {[[c.ventes, "Ventes", C.teal], [fmtEur(c.comm), "Commission", C.golddm], [c.leads, "Leads", C.blue], [c.zone || "—", "Zone", C.mid]].map(([v, l, col]) => (
                  <div key={l} style={{ background: C.gray2, borderRadius: 6, padding: "7px 8px" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: col, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
                    <div style={{ fontSize: 9, color: C.muted, textTransform: "uppercase", letterSpacing: ".04em" }}>{l}</div>
                  </div>
                ))}
              </div>
              {/* Bloc réseau : parrain + filleuls */}
              <div style={{ padding: "8px 10px", background: C.navylt + "88", borderRadius: 8, marginBottom: 10, fontSize: 11 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: nbFilleuls > 0 ? 4 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0 }}>
                    <span style={{ color: C.mid, flexShrink: 0 }}>👤 Parrain :</span>
                    {parr ? (
                      <span style={{ fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{parr.nom}</span>
                    ) : (
                      <span style={{ color: C.mid, fontStyle: "italic" }}>Direct AUPRÉA</span>
                    )}
                  </div>
                </div>
                {nbFilleuls > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <span style={{ color: C.mid }}>🌐 Filleuls :</span>
                    <span style={{ fontWeight: 600, color: C.teal }}>{nbFilleuls} consultant{nbFilleuls > 1 ? "s" : ""}</span>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <div style={{ width: 7, height: 7, borderRadius: "50%", background: c.statut === "Actif" ? C.green : C.amber }} />
                  <span style={{ fontSize: 11, fontWeight: 500, color: c.statut === "Actif" ? C.green : C.amber }}>{{ en_integration: "En intégration", actif: "Actif", suspendu: "Suspendu" }[c.statut] || c.statut}</span>
                </div>
                <Badge text={c.palier} />
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: isAdmin ? 10 : 0 }}>{(c.formule || "").replace("Conseiller", "Consultant")} · Depuis le {c.since}</div>
              {isAdmin && (
                <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                  <Btn small onClick={() => onOpen(c.id)} style={{ flex: 1 }}>✎ Modifier</Btn>
                  <Btn small variant="red" onClick={() => { if (window.confirm(`Supprimer ${c.nom} ?`)) onDelete(c.id); }}>×</Btn>
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 40, color: C.muted }}>Aucun consultant trouvé</div>}
      </div>
    </>
  );
};

// ── COMMISSIONS ──────────────────────────────────────────────────────────────
const CommissionsPage = ({ role }) => {
  const [data, setData] = useState(null);
  useEffect(() => { commissionsApi.get().then(setData).catch(console.error); }, []);
  if (!data) return <Spinner />;
  const isAdmin = role === "admin";
  return (
    <>
      <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, borderRadius: 14, padding: 24, color: "#fff", marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>
          {isAdmin ? "Total commissions à verser" : "Ma commission"}
        </div>
        <div style={{ fontSize: 40, fontWeight: 700, color: C.gold, lineHeight: 1 }}>{fmtEur(data.current)}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", marginTop: 4 }}>
          {isAdmin ? `${data.conseillers?.length || 0} consultants · versement le 30/06` : `palier ${data.conseiller?.palier || "—"} · versement le 30/06`}
        </div>
        {isAdmin && data.conseillers && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, marginTop: 20 }}>
            {data.conseillers.map(c => (
              <div key={c.id} style={{ background: "rgba(255,255,255,.06)", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: c.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: "#fff" }}>{c.initials}</div>
                  <span style={{ fontSize: 11, color: "rgba(255,255,255,.7)" }}>{c.nom.split(" ")[0]}</span>
                </div>
                <div style={{ fontSize: 18, fontWeight: 600, color: "#fff" }}>{fmtEur(c.comm)}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".04em", marginTop: 2 }}>{c.palier}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <SectionLabel>Historique des versements</SectionLabel>
      <TableCard>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead><tr>{["Période", "CA total", "Commissions", "Nb consultants", "Statut"].map(h => (
            <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: ".07em", borderBottom: `.5px solid ${C.border}`, background: C.gray2 }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {(data.historique || []).map(m => (
              <tr key={m.m} style={{ borderBottom: `.5px solid ${C.border}` }}>
                <td style={{ padding: "12px 16px", fontWeight: 600 }}>{m.m}</td>
                <td style={{ padding: "12px 16px" }}>{fmtEur(m.ca)}</td>
                <td style={{ padding: "12px 16px", fontWeight: 600, color: C.golddm }}>{fmtEur(m.comm)}</td>
                <td style={{ padding: "12px 16px" }}>{m.nb}</td>
                <td style={{ padding: "12px 16px" }}><Badge text={m.statut} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </>
  );
};

// ── CATALOGUE (conseiller) ────────────────────────────────────────────────────
const Catalogue = null; // (ancien) remplacé par CatalogueConseiller — voir Catalogue.jsx
void Catalogue;

// ── CONSEILLER DASHBOARD ──────────────────────────────────────────────────────
const RelanceWidget = ({ onOpenSuivi }) => {
  const [notifs, setNotifs] = useState([]);
  const [aRappeler, setARappeler] = useState([]);
  useEffect(() => {
    const tok = localStorage.getItem("auprea_token");
    const cfg = { headers: { Authorization: `Bearer ${tok}` } };
    axios.get(`${API}/consultant/notifications`, cfg).then(r => setNotifs(r.data)).catch(() => {});
    axios.get(`${API}/consultant/a-rappeler`, cfg).then(r => setARappeler(r.data)).catch(() => {});
  }, []);
  if (notifs.length === 0 && aRappeler.length === 0) return null;
  const sendWA = async (n) => {
    const tok = localStorage.getItem("auprea_token");
    const cfg = { headers: { Authorization: `Bearer ${tok}` } };
    const tel = (n.client_telephone || "").replace(/\D/g, "");
    const msg = encodeURIComponent(n.message_prerempli || "");
    window.open(`https://wa.me/${tel}?text=${msg}`, "_blank");
    await axios.post(`${API}/consultant/notifications/${n.id}/mark-sent`, {}, cfg);
    setNotifs(notifs.filter(x => x.id !== n.id));
  };
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14, marginBottom: 24 }}>
      {notifs.length > 0 && (
        <div style={{ background: C.goldlt, border: `1px solid ${C.gold}`, borderRadius: 12, padding: 18 }} data-testid="dash-notif-widget">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.golddm, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>💬 Relances WhatsApp à envoyer</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginBottom: 12 }}>{notifs.length}</div>
          {notifs.slice(0, 3).map(n => (
            <div key={n.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: `.5px dashed ${C.gold}44` }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{n.client_prenom} {n.client_nom}</div>
                <div style={{ fontSize: 10, color: C.golddm }}>J+{n.j} · {n.client_telephone}</div>
              </div>
              <Btn small variant="gold" onClick={() => sendWA(n)} data-testid={`notif-send-${n.id}`}>Envoyer</Btn>
            </div>
          ))}
        </div>
      )}
      {aRappeler.length > 0 && (
        <div style={{ background: C.redlt, border: `1px solid ${C.red}`, borderRadius: 12, padding: 18 }} data-testid="dash-arappeler-widget">
          <div style={{ fontSize: 11, fontWeight: 700, color: C.red, textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 4 }}>📞 À rappeler (relances terminées)</div>
          <div style={{ fontSize: 24, fontWeight: 700, color: C.navy, fontFamily: "'Playfair Display', serif", marginBottom: 12 }}>{aRappeler.length}</div>
          {aRappeler.slice(0, 3).map(p => (
            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: `.5px dashed ${C.red}44` }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.navy }}>{p.prenom} {p.nom}</div>
                <a href={`tel:${p.telephone}`} style={{ fontSize: 11, color: C.red, textDecoration: "underline" }} data-testid={`arappeler-call-${p.id}`}>{p.telephone || p.email}</a>
              </div>
              <Btn small onClick={() => onOpenSuivi && onOpenSuivi(p.id)} data-testid={`arappeler-open-${p.id}`}>Voir</Btn>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ConseillerDashboard = ({ user, data, leads, conseillers, prestations, onNavigate, onAction }) => {
  const me = conseillers.find(c => c.id === user.id) || {};
  const myCA = data?.totalCA || 0;
  const myComm = data?.totalComm || 0;
  const ventes = me.ventes || 0;
  const nextTarget = ventes >= 6 ? 11 : 6;
  const nextName = ventes >= 6 ? "Expert" : "Confirmé";
  return (
    <>
      <Alert type="success">
        <span>🎉</span>
        <span>Bienvenue {user.nom.split(" ")[0]} ! Palier <strong>{me.palier || "Starter"}</strong>. Il vous reste <strong>{Math.max(0, nextTarget - ventes)} ventes</strong> pour atteindre {nextName}.</span>
      </Alert>
      <RelanceWidget onOpenSuivi={() => onNavigate("parcours")} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <KpiCard label="Mon CA ce mois" value={fmtEur(myCA)} delta={`${ventes} ventes · palier ${me.palier || "Starter"}`} accentColor={C.teal} />
        <KpiCard label="Ma commission" value={fmtEur(myComm)} delta="Versement le 30/06" accentColor={C.golddm} />
        <KpiCard label="Leads déclarés" value={leads.length} delta={`${data?.convertis || 0} convertis`} deltaUp={true} accentColor={C.blue} />
        <KpiCard label="Mon palier" value={me.palier || "Starter"} delta={`objectif : ${nextName}`} accentColor={C.purp} />
      </div>
      <SectionLabel>Progression vers {nextName}</SectionLabel>
      <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: C.mid, marginBottom: 10 }}>Ventes ce mois : <strong style={{ color: C.navy }}>{ventes} / {nextTarget}</strong></div>
        <ProgBar value={ventes} max={nextTarget} color={C.gold} />
      </div>
      <SectionLabel>Actions rapides</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {[
          { color: C.gold, label: "🧭 Nouveau parcours", desc: "Démarrer un bilan client complet (fiche + questionnaire + préconisations)", cta: "+ Nouveau parcours client", action: () => onNavigate("parcours"), primary: true },
          { color: C.red, label: "🔴 Leads en attente", desc: `${leads.filter(l => l.a_relancer).length} lead(s) à relancer`, cta: "Voir mes leads", action: () => onNavigate("leads") },
          { color: C.amber, label: "🟡 Nouveau prospect", desc: "Déclarez un nouveau lead en 30 secondes", cta: "Déclarer un lead", action: onAction },
          { color: C.teal, label: "🟢 Catalogue", desc: `Présentez les ${(prestations || []).filter(p => p.actif).length} prestations AUPRÉA`, cta: "Voir le catalogue", action: () => onNavigate("catalogue") },
        ].map(item => (
          <div key={item.label} style={{ background: item.primary ? C.goldlt : "#fff", border: `.5px solid ${C.border}`, borderLeft: `3px solid ${item.color}`, borderRadius: 10, padding: 14 }} data-testid={item.primary ? "dash-nouveau-parcours-card" : undefined}>
            <div style={{ fontSize: 10, fontWeight: 700, color: item.color, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{item.label}</div>
            <div style={{ fontSize: 11, color: C.mid, marginBottom: 12 }}>{item.desc}</div>
            <Btn small variant={item.primary ? "gold" : undefined} onClick={item.action} data-testid={item.primary ? "dash-nouveau-parcours-btn" : undefined}>{item.cta}</Btn>
          </div>
        ))}
      </div>
    </>
  );
};

// ── AUX PAGES ────────────────────────────────────────────────────────────────
const ReseauPage = ({ user }) => (
  <>
    <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 100%)`, borderRadius: 14, padding: 24, color: "#fff", marginBottom: 20 }}>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>Revenus réseau ce mois</div>
      <div style={{ fontSize: 40, fontWeight: 700, color: C.gold }}>0 €</div>
      <div style={{ fontSize: 12, color: "rgba(255,255,255,.5)", marginTop: 4 }}>Aucun filleul actif · recrutez pour activer vos revenus passifs</div>
    </div>
    <Alert type="info"><span>💡</span><span>Chaque consultant que vous recrutez rapporte <strong>10% de ses ventes</strong>. Code parrainage : <strong>{user.id}</strong></span></Alert>
  </>
);

const RessourcesPage = () => {
  const docs = [
    { name: "Guide d'entretien — Consultation initiale", type: "DOCX", icon: "📋", color: C.blue },
    { name: "Fiche présentation des 5 services", type: "PDF", icon: "📄", color: C.teal },
    { name: "Plan de compensation économique", type: "PPTX", icon: "📊", color: C.purp },
    { name: "Script de présentation AUPRÉA", type: "PDF", icon: "🎙️", color: C.amber },
    { name: "Aide-mémoire objections", type: "PDF", icon: "🛡️", color: C.red },
    { name: "Fiche de suivi prospect", type: "DOCX", icon: "👤", color: C.navy2 },
  ];
  return (
    <>
      <SectionLabel>Kit terrain complet</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
        {docs.map(d => (
          <div key={d.name} style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 10, padding: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, background: d.color + "18", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>{d.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{d.name}</div>
              <div style={{ fontSize: 10, fontWeight: 600, color: d.color, marginTop: 2 }}>{d.type}</div>
            </div>
            <Btn small>↓</Btn>
          </div>
        ))}
      </div>
    </>
  );
};

const ComingSoon = ({ page }) => (
  <div style={{ textAlign: "center", padding: "80px 20px" }}>
    <div style={{ fontSize: 48, marginBottom: 16, opacity: .3 }}>🚧</div>
    <div style={{ fontSize: 16, fontWeight: 600, color: C.mid, marginBottom: 8 }}>{PAGE_TITLES[page]} — Prochainement</div>
  </div>
);

// ── LOGIN ─────────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const submit = async () => {
    setLoading(true); setErr("");
    try {
      const data = await auth.login(email, password);
      localStorage.setItem("auprea_token", data.token);
      onLogin(data.user);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Erreur de connexion");
    } finally { setLoading(false); }
  };
  return (
    <>
    <div style={{ position: "fixed", inset: 0, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "rgba(255,255,255,.04)", border: `1px solid rgba(200,144,44,.2)`, borderRadius: 20, padding: 44, width: 420, maxWidth: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 38, color: "#fff", letterSpacing: ".04em" }}>
            <span>AU</span><span style={{ color: C.gold }}>PRÉA</span>
          </div>
          <div style={{ fontSize: 12, color: C.gold, fontStyle: "italic", marginTop: 4 }}>vous accompagne</div>
        </div>
        {err && <div style={{ background: "rgba(153,27,27,.2)", color: "#fca5a5", border: "1px solid rgba(153,27,27,.4)", borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 14 }}>{err}</div>}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.55)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>Email</label>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" style={{ width: "100%", background: "rgba(255,255,255,.06)", border: `1px solid rgba(255,255,255,.14)`, borderRadius: 8, padding: "11px 14px", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,.55)", letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 6 }}>Mot de passe</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" style={{ width: "100%", background: "rgba(255,255,255,.06)", border: `1px solid rgba(255,255,255,.14)`, borderRadius: 8, padding: "11px 14px", color: "#fff", fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ textAlign: "right", marginBottom: 18 }}>
          <button onClick={() => setShowForgot(true)} style={{ background: "none", border: "none", color: C.gold, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
            Mot de passe oublié ?
          </button>
        </div>
        <button disabled={loading} onClick={submit} style={{ width: "100%", background: C.gold, color: C.navy, border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 700, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", opacity: loading ? .7 : 1 }}>
          {loading ? "Connexion..." : "Se connecter"}
        </button>
      </div>
    </div>
    {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}
    </>
  );
};

// ── APP ROOT ──────────────────────────────────────────────────────────────────
export default function AUPREAApp() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  useEffect(() => { const f = (e) => setPage(e.detail); window.addEventListener("auprea-nav", f); return () => window.removeEventListener("auprea-nav", f); }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [openLeadId, setOpenLeadId] = useState(null);
  const [openConseillerId, setOpenConseillerId] = useState(null);
  const [openParcoursId, setOpenParcoursId] = useState(null);
  const [newParcoursOpen, setNewParcoursOpen] = useState(false);
  const [invitationResult, setInvitationResult] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [urlRoute, setUrlRoute] = useState(() => {
    const path = window.location.pathname;
    const setupM = path.match(/^\/setup\/([\w-]+)$/i);
    const resetM = path.match(/^\/reset\/([a-f0-9]+)$/i);
    if (setupM) return { type: "setup", token: setupM[1] };
    if (resetM) return { type: "reset", token: resetM[1] };
    return null;
  });

  const [conseillers, setConseillers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsKey, setLeadsKey] = useState(0);
  const [prestations, setPrestations] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);

  const refreshAll = useCallback(async () => {
    try {
      const [c, l, d, p] = await Promise.all([
        conseillersApi.list(), leadsApi.list(), dashboardApi.get(), prestationsApi.list(),
      ]);
      setConseillers(c); setLeads(l); setDashboardData(d); setPrestations(p);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("auprea_token");
    if (!token) { setBootstrapping(false); return; }
    auth.me().then(setUser).catch(() => localStorage.removeItem("auprea_token")).finally(() => setBootstrapping(false));
  }, []);

  useEffect(() => { if (user) refreshAll(); }, [user, refreshAll]);
  const [nbValidations, setNbValidations] = useState(0);
  const [nbMissions, setNbMissions] = useState(0);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/missions/count`, { headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } })
      .then(r => setNbMissions(r.data.retard + (user.role === "admin" ? r.data.a_relire : 0))).catch(() => {});
  }, [user, page]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const tick = () => axios.get(`${API}/admin/validations/count`, { headers: { Authorization: `Bearer ${localStorage.getItem("auprea_token")}` } })
      .then(r => setNbValidations(r.data.count)).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => clearInterval(t);
  }, [user, page]);
  useEffect(() => { if (user && ["conseillers", "cotisations", "leads"].includes(page)) refreshAll(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = (u) => { setUser(u); setPage("dashboard"); };
  const handleLogout = () => {
    localStorage.removeItem("auprea_token");
    setUser(null); setConseillers([]); setLeads([]); setDashboardData(null); setPrestations([]);
  };
  const handleAction = () => setModal(user?.role === "admin" ? "conseiller" : "lead");
  const leadsChanged = () => { refreshAll(); setLeadsKey(k => k + 1); };
  const deleteConseiller = async (id) => { await conseillersApi.remove(id); refreshAll(); };

  if (bootstrapping) return <div style={{ position: "fixed", inset: 0, background: C.navy, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, fontFamily: "'Playfair Display', serif", fontSize: 24, letterSpacing: ".05em" }}>AUPRÉA</div>;

  // Routes publiques (setup/reset via URL)
  if (urlRoute?.type === "setup") {
    return <SetupPasswordPage token={urlRoute.token}
      onSuccess={() => { setUrlRoute({ type: "setup-ok" }); window.history.replaceState({}, "", "/"); }} />;
  }
  if (urlRoute?.type === "reset") {
    return <ResetPasswordPage token={urlRoute.token}
      onSuccess={() => { setUrlRoute({ type: "reset-ok" }); window.history.replaceState({}, "", "/"); }} />;
  }
  if (urlRoute?.type === "setup-ok") {
    return <AuthSuccess title="Compte activé !" message="Vous pouvez maintenant vous connecter avec votre nouveau mot de passe." onLogin={() => setUrlRoute(null)} />;
  }
  if (urlRoute?.type === "reset-ok") {
    return <AuthSuccess title="Mot de passe modifié !" message="Connectez-vous avec votre nouveau mot de passe." onLogin={() => setUrlRoute(null)} />;
  }

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  const isAdmin = user.role === "admin";

  // Protection RECRUE : si access_actif===false, afficher UNIQUEMENT la page d'intégration
  if (!isAdmin && user.access_actif === false) {
    return (
      <div style={{ minHeight: "100vh", background: C.gray2 }}>
        <div style={{ background: "#fff", borderBottom: `.5px solid ${C.border}`, padding: "14px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: C.navy }}>AUPRÉA</div>
          <Btn onClick={handleLogout}>Se déconnecter</Btn>
        </div>
        <div style={{ padding: 24 }}><EspaceIntegration /></div>
      </div>
    );
  }
  const badges = isAdmin
    ? { leads: leads.filter(l => l.dormant).length, validations: nbValidations, missions: nbMissions }
    : { leads: leads.filter(l => l.a_relancer).length, missions: nbMissions };

  const renderPage = () => {
    const props = { user, role: user.role, userId: user.id, onNavigate: setPage, onAction: handleAction };
    switch (page) {
      case "dashboard":
        return isAdmin
          ? <AdminDashboardV2 onNavigate={setPage} />
          : <ConseillerDashboardV2 onNavigate={setPage} onDemarrerBilan={(rid) => { setPage("parcours"); }} />;
      case "dossiers-admin":
        return isAdmin ? <DossiersAdmin /> : <ComingSoon page={page} />;
      case "virements":
        return isAdmin ? <FacturationAdmin conseillers={conseillers} initialTab="attente" /> : <ComingSoon page={page} />;
      case "conseillers":
        return <ConseillersList conseillers={conseillers} onAdd={() => setModal("conseiller")} onDelete={deleteConseiller} onOpen={(id) => setOpenConseillerId(id)} isAdmin={isAdmin} />;
      case "leads":
        return <LeadsPage conseillers={conseillers} onAddLead={() => setModal("lead")} onOpen={(id) => setOpenLeadId(id)} isAdmin={isAdmin} refreshKey={leadsKey} />;
      case "commissions":
        return isAdmin ? <CommissionsAdminV2 /> : <MesCommissionsV2 onOpenLead={(id) => setOpenLeadId(id)} />;
      case "commissions_valider":
        return isAdmin ? <CommissionsValider /> : null;
      case "cotisations":
        return isAdmin ? <CotisationsAdminV2 /> : <MaCotisation />;
      case "bilans-clients":
        return isAdmin ? <BilansAdmin /> : null;
      case "reseau":
        return isAdmin ? <ReseauAdminV2 /> : <MonEquipeRecruteur />;
      case "fiches-services":
        return <MesFichesServices />;
      case "fiches-services-admin":
        return isAdmin ? <FichesServicesAdmin /> : null;
      case "reformulations-admin":
        return isAdmin ? <ReformulationsAdmin /> : null;
      case "services-admin":
        return isAdmin ? <ServicesAdmin /> : null;
      case "partenaires":
        return isAdmin ? <PartenairesAdmin /> : <ComingSoon page={page} />;
      case "annuaire-partenaires":
        return <AnnuairePartenaires />;
      case "notifications-admin":
        return isAdmin ? <NotificationsAdmin onNavigate={setPage} /> : null;
      case "mes-services":
        return <MesServices />;
      case "catalogue":
        return <CatalogueConseiller onDemarrerParcours={(type) => {
          window.sessionStorage.setItem("auprea_new_parcours_type", type);
          setNewParcoursOpen(true);
          setPage("parcours");
        }} />;
      case "backoffice":
        return isAdmin ? <BackOffice conseillers={conseillers} /> : <ComingSoon page={page} />;
      case "missions":
        return <MissionsPage isAdmin={isAdmin} />;
      case "disponibilites":
        return <Disponibilites user={user} />;
      case "factures":
        return isAdmin ? <FacturationAdmin conseillers={conseillers} /> : <FacturesPage />;
      case "parcours":
        if (openParcoursId || newParcoursOpen) {
          return <ParcoursClient
            user={user}
            initialParcoursId={openParcoursId}
            onClose={() => { setOpenParcoursId(null); setNewParcoursOpen(false); }}
            onFinished={() => { setOpenParcoursId(null); setNewParcoursOpen(false); refreshAll(); }}
          />;
        }
        return <ParcoursList key={leadsKey} user={user}
          onOpenParcours={(id) => setOpenParcoursId(id)}
          onNouveau={() => setNewParcoursOpen(true)} />;
      case "ressources":
        return <KitTerrain />;
      case "kit-admin":
        return isAdmin ? <KitTerrainAdmin /> : <ComingSoon page={page} />;
      case "prestations":
        return isAdmin ? <PrestationsAdmin /> : <ComingSoon page={page} />;
      case "parametres":
        return isAdmin ? <ReglagesAdmin /> : <ComingSoon page={page} />;
      case "validations":
        return isAdmin ? <ValidationsAdmin /> : <ComingSoon page={page} />;
      case "modeles":
        return isAdmin ? <ModelesAdmin /> : <ComingSoon page={page} />;
      case "rgpd-admin":
        return isAdmin ? <RgpdDemandes /> : <ComingSoon page={page} />;
      case "candidatures":
        return isAdmin ? <CandidaturesAdmin /> : <ComingSoon page={page} />;
      case "reseau-tree":
        return isAdmin ? <ReseauAdminV2 /> : <ComingSoon page={page} />;
      case "mon-integration":
        return <EspaceIntegration />;
      case "integrations":
        return isAdmin ? <IntegrationsAdmin /> : <ComingSoon page={page} />;
      case "pieces":
        return isAdmin ? <PiecesAControler /> : <ComingSoon page={page} />;
      case "mes-recrues":
        return <MesRecrues />;
      case "modeles-contrats":
        return isAdmin ? <ModelesContrats /> : <ComingSoon page={page} />;
      case "contrats-signes":
        return isAdmin ? <ContratsSignes /> : <ComingSoon page={page} />;
      case "mes-contrats":
        return <div style={{ background: "#fff", borderRadius: 14, padding: 22 }}><SignatureContrats /></div>;
      case "invitations":
        return isAdmin ? <PendingInvitations /> : <ComingSoon page={page} />;
      case "mon-compte":
        return <MonCompte user={user} myConseiller={conseillers.find(c => c.id === user.id)} onRefresh={refreshAll} />;
      default:
        return <ComingSoon page={page} />;
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.gray, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", color: C.text }}>
      <style>{`@media (max-width: 768px){.aup-sidebar{position:fixed!important;left:0;top:0;z-index:300;transform:translateX(-100%);transition:transform .2s ease;overflow-y:auto!important}.aup-sidebar.open{transform:none;box-shadow:4px 0 24px rgba(0,0,0,.25)}.aup-burger{display:inline-flex!important}.aup-topbar{padding:0 12px!important}.aup-topbar-date{display:none}.aup-topbar-actions button{font-size:12px}.aup-main-pad{padding:14px 12px!important}.aup-backdrop{display:block!important}}`}</style>
      {menuOpen && <div className="aup-backdrop" onClick={() => setMenuOpen(false)} data-testid="menu-mobile-backdrop" style={{ display: "none", position: "fixed", inset: 0, background: "rgba(0,0,0,.35)", zIndex: 250 }} />}
      <Sidebar role={user.role} user={user} activePage={page} onNavigate={(p) => { setPage(p); setMenuOpen(false); }} onLogout={handleLogout} badges={badges} open={menuOpen} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Topbar page={page} role={user.role} onAction={handleAction} nbValidations={nbValidations} onNavigate={setPage} onMenu={() => setMenuOpen(true)} />
        <div className="aup-main-pad" style={{ padding: "24px 28px", flex: 1, minWidth: 0 }}>{renderPage()}</div>
      </div>
      {modal === "lead" && <DeclarerLeadModal onClose={() => setModal(null)} onSaved={leadsChanged} onOpenLead={(id) => setOpenLeadId(id)} conseillers={conseillers} isAdmin={isAdmin} />}
      {modal === "conseiller" && <ConseillerModal onClose={() => setModal(null)} onSaved={refreshAll} conseillers={conseillers} onCreated={setInvitationResult} />}
      {invitationResult && <InvitationLinkModal result={invitationResult} onClose={() => setInvitationResult(null)} />}
      {openLeadId && <LeadDetail leadId={openLeadId} isAdmin={isAdmin} onClose={() => setOpenLeadId(null)} onSaved={leadsChanged}
        onFixerRdv={(l) => { sessionStorage.setItem("auprea_lead_rdv", JSON.stringify(l)); setOpenLeadId(null); setOpenParcoursId(null); setNewParcoursOpen(false); setPage("parcours"); setLeadsKey(k => k + 1); }}
        onDemarrerBilan={(l) => { sessionStorage.setItem("auprea_lead_bilan", JSON.stringify(l)); setOpenLeadId(null); setOpenParcoursId(null); setNewParcoursOpen(true); setPage("parcours"); }} />}
      {openConseillerId && <ConseillerDetail conseillerId={openConseillerId} conseillers={conseillers} onClose={() => setOpenConseillerId(null)} onSaved={refreshAll} />}
    </div>
  );
}
